import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetTaskTemplatesLazyQuery } from 'types/atlas-graphql';
import { useFeatures } from 'utils/features';

import { formatTemplatesMenuItems, formatTemplatesSelectOptions } from './helpers';
import { StyledMenu, StyledSelect } from './TaskTemplatesChooser.style';
import { type ITaskTemplatesChooserProps } from './types';

export const TaskTemplatesChooser: React.FunctionComponent<ITaskTemplatesChooserProps> = ({
  workContainerTypes,
  onClickHandler,
  displayType,
  taskStepsRequired = false,
  costManagementRequired = false,
  disabled = false,
  menuTitle,
}) => {
  const { t } = useTranslation();
  const { TASK_TEMPLATES } = useFeatures().features;

  const [getAllTaskTemplates, { data: { getTaskTemplates: templatesData = null } = {} }] =
    useGetTaskTemplatesLazyQuery();

  useEffect(() => {
    if (TASK_TEMPLATES) {
      getAllTaskTemplates({
        variables: {
          workContainerTypes,
          taskStepsRequired,
        },
      });
    }
  }, [TASK_TEMPLATES, getAllTaskTemplates, workContainerTypes, taskStepsRequired]);

  const filteredTemplates = useMemo(
    () =>
      costManagementRequired
        ? templatesData?.filter(
            template =>
              (template.costResourceActiveDefaults &&
                template.costResourceActiveDefaults.length > 0) ||
              (template.rateDefaults && template.rateDefaults.length > 0)
          )
        : templatesData,
    [templatesData, costManagementRequired]
  );

  const noTemplatesText = t('workOrder.noTemplatesCreated', 'No Templates have been created');

  return displayType === 'menu' ? (
    <StyledMenu
      onClick={e => onClickHandler(e)}
      items={[
        {
          key: 'templates-menu',
          label: menuTitle ?? t('settings.templates', 'Templates'),
          children: filteredTemplates?.length
            ? [
                {
                  type: 'group',
                  key: 'templates',
                  style: { overflowY: 'auto', maxHeight: '300px' },
                  children: formatTemplatesMenuItems(filteredTemplates),
                },
              ]
            : [{ key: 'none', disabled: true, label: noTemplatesText }],
        },
      ]}
    />
  ) : (
    <StyledSelect
      data-testid="template-chooser"
      onSelect={e => onClickHandler(e)}
      placeholder={t('taskTemplates.selectTemplate', 'Select Template...')}
      disabled={disabled}
      allowClear={true}
      onClear={() => onClickHandler(undefined)}
      options={
        filteredTemplates?.length === 0
          ? [{ value: 'none', label: noTemplatesText, disabled: true }]
          : formatTemplatesSelectOptions(filteredTemplates ?? [])
      }
    />
  );
};
